import { Injectable } from '@angular/core';
import type { TableCardConfig } from '../models/table-config.model';
import { parseChartVisualizationInput } from '../utils/parse-chart-visualization-input.util';
import { tablePageSizeFromConfig } from '../utils/table-presentation-visualization.util';
import {
	ValidationWarning,
	WarningSeverity,
} from './card-config-change.types';

@Injectable({
	providedIn: 'root',
})
export class TableConfigValidator {
	validate(config: TableCardConfig): ValidationWarning[] {
		const warnings: ValidationWarning[] = [];
		const metrics = config.data?.metrics ?? [];
		const dimensions = config.data?.dimensions ?? [];
		const hasMetric = metrics.some(
			m => typeof m?.name === 'string' && m.name.trim().length > 0
		);
		const hasDimension = dimensions.some(
			d => typeof d?.name === 'string' && d.name.trim().length > 0
		);

		if (!hasMetric && !hasDimension) {
			warnings.push({
				code: 'TABLE_NO_DIMENSION_OR_METRIC',
				message:
					'Add at least one dimension or metric in the Data tab.',
				severity: WarningSeverity.Blocking,
				field: 'data.dimensions',
			});
		}

		const viz = parseChartVisualizationInput(config.visualization);
		const vizColumns = Array.isArray(
			viz &&
				typeof viz === 'object' &&
				!Array.isArray(viz) &&
				(viz as { columns?: unknown }).columns
				? (viz as { columns: unknown[] }).columns
				: undefined
		)
			? ((viz as { columns: unknown[] }).columns ?? [])
			: [];
		const overrideMissingId = vizColumns.some(entry => {
			if (!entry || typeof entry !== 'object') {
				return false;
			}
			const id = (entry as { id?: unknown }).id;
			return typeof id !== 'string' || !id.trim();
		});
		if (overrideMissingId) {
			warnings.push({
				code: 'TABLE_COLUMN_OVERRIDE_MISSING_ID',
				message:
					'One or more visualization column overrides are missing `id` and will be ignored.',
				severity: WarningSeverity.Warning,
				field: 'visualization.columns',
			});
		}

		const pageSize = tablePageSizeFromConfig(config);
		if (!Number.isFinite(pageSize) || pageSize < 1) {
			warnings.push({
				code: 'TABLE_INVALID_PAGE_SIZE',
				message: 'Page size must be at least 1.',
				severity: WarningSeverity.Warning,
				field: 'features.pagination.pageSize',
			});
		}

		return warnings;
	}
}
